import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  ListItemText,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { SelectInput } from "components/HookFormComponents/SelectInput";
import { NumberInput } from "components/HookFormComponents/NumberInput";
import { FormProvider, useForm } from "react-hook-form";
import ConfigSectorsUtils from "services/configSectors/utils";
import { FormattedMessage, useIntl } from "react-intl";
import ConfigSectorsActioner from "services/configSectors/actioner";
import styled from "@emotion/styled";
import ParkkiTheme from "themes/ParkkiTheme";
import ParkkiButton from "components/Button";

const Title = styled.div`
  font-size: ${ParkkiTheme.fontSizes.small};
  font-weight: bolder;
  margin-top: ${ParkkiTheme.spacings.tiny};
  margin-bottom: ${ParkkiTheme.spacings.xsmall};
  font-family: "Source Sans Pro", sans-serif;
`;

const StyledButton = styled(ParkkiButton)`
  height: 50%;
  margin-top: ${ParkkiTheme.spacings.tiny};
`;

const StyledNumber = styled.span`
  color: ${ParkkiTheme.colors.turquoise};
`;

function ConfigSectorFormContainer({ sector, data, updateSector }) {
  const [open, setOpen] = useState(false);
  const methods = useForm({
    defaultValues: {
      type: ConfigSectorsUtils.getDefaultTypeBySector(sector),
      value: ConfigSectorsUtils.getDefaultValueBySector(sector, data),
    },
    reValidateMode: "onBlur",
  });
  const intl = useIntl();
  const currentType = methods.watch("type");
  const currentValue = methods.watch("value");
  useEffect(() => {
    methods.setValue(
      "value",
      ConfigSectorsUtils.getValueFromType(currentType, sector, data)
    );
  }, [currentType, data, methods, sector]);
  async function handleSubmit() {
    setOpen(true);
  }

  async function update() {
    updateSector(
      sector.id,
      methods.getValues(),
      ConfigSectorsUtils.getValueFromType(currentType, sector, data)
    );
  }
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      <Box sx={{ pt: 1, width: "10%" }}>
        <Title>{sector.name}</Title>
      </Box>
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(handleSubmit)}
          sx={{
            display: "flex",
            flex: 2,
            flexDirection: "row",
            pl: 2,
            gap: 2,
            width: "100%",
          }}
          noValidate
        >
          <SelectInput
            label={<FormattedMessage id="configSectorsScene.labels.type" />}
            name="type"
            items={ConfigSectorsUtils.getTypesAllowedBySector(sector, intl)}
            sx={{ flex: 1 }}
          />
          <NumberInput
            label={<FormattedMessage id="configSectorsScene.labels.value" />}
            name="value"
            required={true}
            sx={{ flex: 1 }}
          />
          <StyledButton
            fullWidth={false}
            type="submit"
            variant="contained"
            label={<FormattedMessage id="configSectorsScene.save" />}
          />
        </Box>
        <Dialog open={open} fullWidth>
          <DialogTitle>
            <FormattedMessage id="configSectorsScene.dialog.title" />
          </DialogTitle>
          <DialogContent sx={{ textAlign: "center" }}>
            <FormattedMessage
              id="configSectorsScene.dialog.content"
              values={{
                value: <StyledNumber>{currentValue}</StyledNumber>,
                type: (
                  <FormattedMessage
                    id={`configSectorsScene.dialog.types.${currentType?.toLowerCase()}`}
                  />
                ),
                old: (
                  <StyledNumber>
                    {ConfigSectorsUtils.getValueFromType(
                      currentType,
                      sector,
                      data
                    )}
                  </StyledNumber>
                ),
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>
              <FormattedMessage id="configSectorsScene.dialog.cancel" />
            </Button>
            <ParkkiButton
              onClick={() => update()}
              fullWidth={false}
              label={
                <FormattedMessage id="configSectorsScene.dialog.confirm" />
              }
            />
          </DialogActions>
        </Dialog>
      </FormProvider>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  data: state.ConfigSectors.sectors.data,
});

const mapDispatchToProps = (dispatch) => ({
  updateSector: (id, data, previousData) =>
    dispatch(ConfigSectorsActioner.updateSector(id, data, previousData)),
});

const ConfigSectorFormContainerConnected = connect(
  mapStateToProps,
  mapDispatchToProps
)(ConfigSectorFormContainer);

function ConfigSectorForm({ sector }) {
  return (
    <ListItem sx={{ pl: 4 }}>
      <ListItemText
        primary={<ConfigSectorFormContainerConnected sector={sector} />}
      />
    </ListItem>
  );
}

export default ConfigSectorForm;
